/**
 * Medication Form Component - FHIR R5 Compliant
 * Three separate sections: Basic Info + Extended Fields, Dosage, Prescription
 */

import { useAuthenticatedImage } from "@/hooks/useAuthenticatedImage";
import {
  Camera,
  Clock,
  FileText,
  Package,
  Pill,
  Save,
  Upload,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { medicationService } from "../../services/api/medicationService";
import { NudgeBanner } from "../ui/NudgeBanner";
import type {
  MedicationFormData,
  MedicationTerminologies,
} from "../../types/medication";
import { CameraCapture } from "./CameraCapture";
import { log } from "@/lib/log";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";

type MedxKey = keyof Translation & `medx${string}`;

/** Dose forms are stored as codes; only the label is translated. */
const DOSE_FORM_OPTIONS: ReadonlyArray<{ value: string; labelKey: MedxKey }> = [
  { value: "tablet", labelKey: "medxDoseFormTablet" },
  { value: "capsule", labelKey: "medxDoseFormCapsule" },
  { value: "syrup", labelKey: "medxDoseFormSyrup" },
  { value: "liquid", labelKey: "medxDoseFormLiquid" },
  { value: "cream", labelKey: "medxDoseFormCream" },
  { value: "injection", labelKey: "medxDoseFormInjection" },
  { value: "infusion", labelKey: "medxDoseFormInfusion" },
  { value: "suppository", labelKey: "medxDoseFormSuppository" },
  { value: "inhaler", labelKey: "medxDoseFormInhaler" },
];

/** MedicationStatement status codes offered in the form. */
const STATUS_OPTIONS: ReadonlyArray<{ value: string; labelKey: MedxKey }> = [
  { value: "recorded", labelKey: "medxStatusActive" },
  { value: "completed", labelKey: "medxStatusCompleted" },
  { value: "stopped", labelKey: "medxStatusStopped" },
  { value: "draft", labelKey: "medxStatusDraft" },
];

/**
 * Dose units. The stored value is the unit code (medical units stay as-is);
 * `labelKey` spells the unit out, and count units show only the label.
 */
const DOSE_UNIT_GROUPS: ReadonlyArray<{
  groupKey: MedxKey;
  units: ReadonlyArray<{ value: string; labelKey: MedxKey; symbol?: string }>;
}> = [
  {
    groupKey: "medxUnitGroupWeight",
    units: [
      { value: "mg", symbol: "mg", labelKey: "medxUnitMg" },
      { value: "g", symbol: "g", labelKey: "medxUnitG" },
      { value: "kg", symbol: "kg", labelKey: "medxUnitKg" },
    ],
  },
  {
    groupKey: "medxUnitGroupVolume",
    units: [
      { value: "ml", symbol: "mL", labelKey: "medxUnitMl" },
      { value: "l", symbol: "L", labelKey: "medxUnitL" },
    ],
  },
  {
    groupKey: "medxUnitGroupCount",
    units: [
      { value: "Tablet", labelKey: "medxUnitTablet" },
      { value: "Capsule", labelKey: "medxUnitCapsule" },
      { value: "Drops", labelKey: "medxUnitDrops" },
      { value: "Piece", labelKey: "medxUnitPiece" },
    ],
  },
  {
    groupKey: "medxUnitGroupUnits",
    units: [
      { value: "IU", symbol: "IU", labelKey: "medxUnitIu" },
      { value: "µg", symbol: "µg", labelKey: "medxUnitMcg" },
    ],
  },
];

/** Count units are stored as English words; show them translated. */
const COUNT_UNIT_KEYS: Record<string, MedxKey> = {
  Tablet: "medxUnitTablet",
  Capsule: "medxUnitCapsule",
  Drops: "medxUnitDrops",
  Piece: "medxUnitPiece",
};

const PACKAGE_SIZE_KEYS: Record<string, MedxKey> = {
  SAMPLE: "medxPackageSizeSample",
  CUSTOM: "medxPackageSizeCustom",
};

type IntakeTimingKey =
  | "morning"
  | "noon"
  | "afternoon"
  | "evening"
  | "night"
  | "bedtime";

const INTAKE_TIMING_OPTIONS: ReadonlyArray<{
  value: IntakeTimingKey;
  labelKey: MedxKey;
}> = [
  { value: "morning", labelKey: "medxTimingMorning" },
  { value: "noon", labelKey: "medxTimingNoon" },
  { value: "afternoon", labelKey: "medxTimingAfternoon" },
  { value: "evening", labelKey: "medxTimingEvening" },
  { value: "night", labelKey: "medxTimingNight" },
  { value: "bedtime", labelKey: "medxTimingBedtime" },
];

interface MedicationFormProps {
  medicationId?: string | undefined;
  onClose: () => void;
  onSave: () => void;
}

/**
 * Both image uploads report the same HttpError-shaped failure. A `catch` binding is
 * `unknown`, so the fields have to be narrowed rather than read directly.
 */
function describeUploadError(
  error: unknown,
  unknownMessage: string,
): {
  message: string;
  response: unknown;
  status: number | undefined;
} {
  const failure = error as {
    message?: string;
    response?: { data?: unknown; status?: number };
  };
  return {
    message: failure?.message ?? unknownMessage,
    response: failure?.response?.data,
    status: failure?.response?.status,
  };
}

export const MedicationForm: React.FC<MedicationFormProps> = ({
  medicationId,
  onClose,
  onSave,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const fieldClass =
    "w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  const [formData, setFormData] = useState<Partial<MedicationFormData>>({
    status: "recorded",
    intakeTiming: {
      morning: false,
      noon: false,
      afternoon: false,
      evening: false,
      night: false,
      bedtime: false,
    },
  });
  const [terminologies, setTerminologies] =
    useState<MedicationTerminologies | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Image upload state
  const [packageImage, setPackageImage] = useState<File | null>(null);
  const [medicationImage, setMedicationImage] = useState<File | null>(null);
  const [packagePreview, setPackagePreview] = useState<string | null>(null);
  const [medicationPreview, setMedicationPreview] = useState<string | null>(
    null,
  );
  // Stored previews are API paths that need the bearer token to load.
  const packagePreviewSrc = useAuthenticatedImage(packagePreview);
  const medicationPreviewSrc = useAuthenticatedImage(medicationPreview);

  // Camera capture state
  const [showPackageCamera, setShowPackageCamera] = useState(false);
  const [showMedicationCamera, setShowMedicationCamera] = useState(false);

  useEffect(() => {
    loadTerminologies();
    if (medicationId) {
      loadMedication();
    }
  }, [medicationId]);

  const loadTerminologies = async () => {
    try {
      const response = await medicationService.getTerminologies();
      setTerminologies(response.data);
    } catch (error: any) {
      console.error("Failed to load terminologies:", error);
    }
  };

  const loadMedication = async () => {
    if (!medicationId) return;
    try {
      setLoading(true);
      const response = await medicationService.getMedicationById(medicationId);
      // Convert FHIR bundle to form data
      const bundle = response.data;
      setFormData({
        name:
          bundle.medication?.tradeName ||
          bundle.medicationStatement.medicationCodeableConcept?.text ||
          "",
        tradeName: bundle.medication?.tradeName || "",
        activeIngredient: bundle.medication?.activeIngredient || "",
        manufacturer: bundle.medication?.manufacturerName || "",
        packageSize: bundle.medication?.packageSize,
        prescriptionPurpose: bundle.medication?.prescriptionPurpose || "",
        doseForm: bundle.medication?.doseForm?.coding?.[0]?.code || "",
        dosageValue:
          Number(bundle.medicationStatement.dosageDetail?.value) || 0,
        dosageUnit: bundle.medicationStatement.dosageDetail?.unit || "",
        intakeTiming: bundle.medicationStatement.intakeTiming || {
          morning: false,
          noon: false,
          afternoon: false,
          evening: false,
          night: false,
          bedtime: false,
        },
        intakeInstructions: bundle.medicationStatement.intakeInstructions || "",
        startDate: bundle.medicationStatement.medicationPeriod?.start || "",
        endDate: bundle.medicationStatement.medicationPeriod?.end || "",
        prescriberName: bundle.medicationStatement.prescriber?.name || "",
        prescriptionCountry:
          bundle.medicationStatement.prescriptionCountry || "",
        status:
          (bundle.medicationStatement.status === "entered-in-error"
            ? "draft"
            : bundle.medicationStatement.status) || "recorded",
      });

      // Load existing images as previews
      if (bundle.medication?.images?.packageImage) {
        setPackagePreview(bundle.medication.images.packageImage);
        log.debug(
          "📦 Loaded package image:",
          bundle.medication.images.packageImage,
        );
      }
      if (bundle.medication?.images?.medicationImage) {
        setMedicationPreview(bundle.medication.images.medicationImage);
        log.debug(
          "💊 Loaded medication image:",
          bundle.medication.images.medicationImage,
        );
      }
    } catch (error: any) {
      console.error("Failed to load medication:", error);
    } finally {
      setLoading(false);
    }
  };

  // Image upload handlers
  const handlePackageImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPackageImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPackagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMedicationImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setMedicationImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMedicationPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePackageImage = async () => {
    // If there's a local file (not yet uploaded), just remove it
    if (packageImage) {
      setPackageImage(null);
      setPackagePreview(null);
      return;
    }

    // If it's an existing image from backend (URL), delete it from MinIO
    if (packagePreview && packagePreview.startsWith("http") && medicationId) {
      if (!confirm(t.medxFormConfirmDeletePackage)) {
        return;
      }

      try {
        log.debug("🗑️ Deleting package image from MinIO...");
        await medicationService.deleteMedicationImage(medicationId, "package");
        setPackagePreview(null);
        log.debug("✅ Package image deleted successfully");
        alert(`✅ ${t.medxFormPackageDeleted}`);
      } catch (error: any) {
        console.error("❌ Failed to delete package image:", error);
        alert(t.medxFormDeleteFailed.replace("{error}", error.message));
      }
    } else {
      // No image to delete
      setPackagePreview(null);
    }
  };

  const removeMedicationImage = async () => {
    // If there's a local file (not yet uploaded), just remove it
    if (medicationImage) {
      setMedicationImage(null);
      setMedicationPreview(null);
      return;
    }

    // If it's an existing image from backend (URL), delete it from MinIO
    if (
      medicationPreview &&
      medicationPreview.startsWith("http") &&
      medicationId
    ) {
      if (!confirm(t.medxFormConfirmDeletePill)) {
        return;
      }

      try {
        log.debug("🗑️ Deleting medication image from MinIO...");
        await medicationService.deleteMedicationImage(
          medicationId,
          "medication",
        );
        setMedicationPreview(null);
        log.debug("✅ Medication image deleted successfully");
        alert(`✅ ${t.medxFormPillDeleted}`);
      } catch (error: any) {
        console.error("❌ Failed to delete medication image:", error);
        alert(t.medxFormDeleteFailed.replace("{error}", error.message));
      }
    } else {
      // No image to delete
      setMedicationPreview(null);
    }
  };

  // Camera capture handlers
  const handlePackageCameraCapture = (file: File) => {
    setPackageImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPackagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleMedicationCameraCapture = (file: File) => {
    setMedicationImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setMedicationPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    log.debug("🔄 Starting medication save process...");
    log.debug(
      "📦 Package Image:",
      packageImage
        ? `${packageImage.name} (${packageImage.size} bytes)`
        : "None",
    );
    log.debug(
      "💊 Medication Image:",
      medicationImage
        ? `${medicationImage.name} (${medicationImage.size} bytes)`
        : "None",
    );

    try {
      // Save or update medication first
      let savedMedicationId: string;
      if (medicationId) {
        log.debug(`🔄 Updating existing medication: ${medicationId}`);
        await medicationService.updateMedication(medicationId, formData);
        savedMedicationId = medicationId;
        log.debug(`✅ Medication updated: ${savedMedicationId}`);
      } else {
        log.debug("🔄 Creating new medication...");
        const response = await medicationService.createMedication(
          formData as MedicationFormData,
        );
        savedMedicationId = response.data.medicationStatement.id || "";
        log.debug(`✅ Medication created: ${savedMedicationId}`);
      }

      // Upload package image if selected
      if (packageImage) {
        try {
          log.debug(
            `📦 Uploading package image for medication ${savedMedicationId}...`,
          );
          log.debug(`📦 File details:`, {
            name: packageImage.name,
            size: packageImage.size,
            type: packageImage.type,
          });
          const result = await medicationService.uploadPackageImage(
            savedMedicationId,
            packageImage,
          );
          log.debug("✅ Package image uploaded successfully:", result);
        } catch (uploadError: unknown) {
          const details = describeUploadError(
            uploadError,
            t.medxFormUnknownError,
          );
          console.error("❌ Failed to upload package image:", uploadError);
          console.error("❌ Error details:", details);
          alert(
            t.medxFormPackageUploadFailed.replace("{error}", details.message),
          );
          // Don't block the save, just log the error
        }
      }

      // Upload medication image if selected
      if (medicationImage) {
        try {
          log.debug(
            `💊 Uploading medication image for medication ${savedMedicationId}...`,
          );
          log.debug(`💊 File details:`, {
            name: medicationImage.name,
            size: medicationImage.size,
            type: medicationImage.type,
          });
          const result = await medicationService.uploadMedicationImage(
            savedMedicationId,
            medicationImage,
          );
          log.debug("✅ Medication image uploaded successfully:", result);
        } catch (uploadError: unknown) {
          const details = describeUploadError(
            uploadError,
            t.medxFormUnknownError,
          );
          console.error("❌ Failed to upload medication image:", uploadError);
          console.error("❌ Error details:", details);
          alert(t.medxFormPillUploadFailed.replace("{error}", details.message));
          // Don't block the save, just log the error
        }
      }

      log.debug("🎉 All operations completed successfully!");

      // Reload medication data to get updated image URLs from backend
      if (savedMedicationId && (packageImage || medicationImage)) {
        log.debug("🔄 Reloading medication data to refresh image previews...");
        try {
          const response =
            await medicationService.getMedicationById(savedMedicationId);
          const bundle = response.data;

          log.debug("📊 Reloaded medication data:", {
            packageImage: bundle.medication?.images?.packageImage,
            medicationImage: bundle.medication?.images?.medicationImage,
          });

          // Update image previews with new URLs from backend
          if (bundle.medication?.images?.packageImage) {
            setPackagePreview(bundle.medication.images.packageImage);
            setPackageImage(null); // Clear file object since it's now uploaded
            log.debug(
              "✅ Package image preview updated:",
              bundle.medication.images.packageImage,
            );
          }
          if (bundle.medication?.images?.medicationImage) {
            setMedicationPreview(bundle.medication.images.medicationImage);
            setMedicationImage(null); // Clear file object since it's now uploaded
            log.debug(
              "✅ Medication image preview updated:",
              bundle.medication.images.medicationImage,
            );
          }

          // Show success message and keep form open to display updated images
          alert(`✅ ${t.medxFormSavedWithImages}`);
          onSave();
          // Don't close immediately - let user see the updated images
          return;
        } catch (reloadError) {
          console.error("❌ Failed to reload medication data:", reloadError);
          // Don't block the save completion
        }
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error("❌ Failed to save medication:", error);
      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        t.medxFormSaveFailed;
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (
    field: keyof MedicationFormData,
    value: string | number | boolean | Date,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateIntakeTiming = (time: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      intakeTiming: {
        ...prev.intakeTiming,
        [time]: checked,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Pill className="w-6 h-6 text-primary-600" />
          {medicationId ? t.medxFormTitleEdit : t.medxFormTitleNew}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.medxFormClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Box 1: Medication Information (Merged Basic + Extended) */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          {t.medxFormSectionInfo}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Medication Name / Trade Name */}
          <div className="md:col-span-2">
            <label
              htmlFor="medication-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormName} <span className="text-red-500">*</span>
            </label>
            <input
              id="medication-name"
              type="text"
              value={formData.name || formData.tradeName || ""}
              onChange={(e) => {
                updateField("name", e.target.value);
                updateField("tradeName", e.target.value);
              }}
              placeholder={t.medxFormNamePlaceholder}
              required
              className={fieldClass}
            />
          </div>

          {/* Active Ingredient */}
          <div>
            <label
              htmlFor="medication-ingredient"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormIngredient}
            </label>
            <input
              id="medication-ingredient"
              type="text"
              value={formData.activeIngredient || ""}
              onChange={(e) => updateField("activeIngredient", e.target.value)}
              placeholder={t.medxFormIngredientPlaceholder}
              className={fieldClass}
            />
          </div>

          {/* Manufacturer */}
          <div>
            <label
              htmlFor="medication-manufacturer"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormManufacturer}
            </label>
            <input
              id="medication-manufacturer"
              type="text"
              value={formData.manufacturer || ""}
              onChange={(e) => updateField("manufacturer", e.target.value)}
              placeholder={t.medxFormManufacturerPlaceholder}
              className={fieldClass}
            />
          </div>

          {/* Package Size */}
          <div>
            <label
              htmlFor="medication-package-size"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormPackageSize}
            </label>
            <select
              id="medication-package-size"
              value={formData.packageSize || ""}
              onChange={(e) => updateField("packageSize", e.target.value)}
              className={fieldClass}
            >
              <option value="">{t.medxFormSelect}</option>
              {terminologies?.packageSizes.map((size) => (
                <option key={size.code} value={size.code}>
                  {PACKAGE_SIZE_KEYS[size.code]
                    ? t[PACKAGE_SIZE_KEYS[size.code]!]
                    : size.display}
                </option>
              ))}
            </select>
          </div>

          {/* Dose Form */}
          <div>
            <label
              htmlFor="medication-dose-form"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormDoseForm}
            </label>
            <select
              id="medication-dose-form"
              value={formData.doseForm || ""}
              onChange={(e) => updateField("doseForm", e.target.value)}
              className={fieldClass}
            >
              <option value="">{t.medxFormSelect}</option>
              {DOSE_FORM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t[option.labelKey]}
                </option>
              ))}
            </select>
          </div>

          {/* Prescription Purpose */}
          <div className="md:col-span-2">
            <label
              htmlFor="medication-prescription-purpose"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormPurpose}
            </label>
            <select
              id="medication-prescription-purpose"
              value={formData.prescriptionPurpose || ""}
              onChange={(e) =>
                updateField("prescriptionPurpose", e.target.value)
              }
              className={fieldClass}
            >
              <option value="">{t.medxFormSelect}</option>
              {terminologies?.prescriptionPurposes.map((purpose) => (
                <option key={purpose.code} value={purpose.display}>
                  {purpose.display}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label
              htmlFor="medication-status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormStatus} <span className="text-red-500">*</span>
            </label>
            <select
              id="medication-status"
              value={formData.status || "recorded"}
              onChange={(e) => updateField("status", e.target.value)}
              required
              className={fieldClass}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t[option.labelKey]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Box 2: Dosage */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg shadow-sm p-6 border-2 border-primary-300">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary-600" />
          {t.medxFormSectionDosage}
        </h2>

        <div className="space-y-6">
          {/* Dose and Unit */}
          <div>
            <div className="block text-sm font-medium text-gray-700 mb-3">
              {t.medxFormDose} <span className="text-red-500">*</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  type="number"
                  aria-label={t.medxFormAmount}
                  value={formData.dosageValue || ""}
                  onChange={(e) =>
                    updateField("dosageValue", Number(e.target.value))
                  }
                  placeholder="500"
                  min="0"
                  step="0.1"
                  required
                  className={fieldClass}
                />
                <p className="text-xs text-gray-500 mt-1">{t.medxFormAmount}</p>
              </div>
              <div>
                <select
                  aria-label={t.medxFormUnit}
                  value={formData.dosageUnit || ""}
                  onChange={(e) => updateField("dosageUnit", e.target.value)}
                  required
                  className={fieldClass}
                >
                  <option value="">{t.medxFormSelectUnit}</option>
                  {DOSE_UNIT_GROUPS.map((group) => (
                    <optgroup key={group.groupKey} label={t[group.groupKey]}>
                      {group.units.map((unit) => (
                        <option key={unit.value} value={unit.value}>
                          {unit.symbol
                            ? `${unit.symbol} (${t[unit.labelKey]})`
                            : t[unit.labelKey]}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{t.medxFormUnit}</p>
              </div>
            </div>
            {formData.dosageValue && formData.dosageUnit && (
              <p className="text-sm text-green-600 mt-2">
                ✓{" "}
                {t.medxFormDosePreview.replace(
                  "{dose}",
                  `${fmt.number(formData.dosageValue)} ${
                    COUNT_UNIT_KEYS[formData.dosageUnit]
                      ? t[COUNT_UNIT_KEYS[formData.dosageUnit]!]
                      : formData.dosageUnit
                  }`,
                )}
              </p>
            )}
          </div>

          {/* Intake Times */}
          <div>
            <div className="block text-sm font-medium text-gray-700 mb-3">
              {t.medxFormIntakeTimes}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {INTAKE_TIMING_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 p-3 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-primary-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.intakeTiming?.[option.value] || false}
                    onChange={(e) =>
                      updateIntakeTiming(option.value, e.target.checked)
                    }
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm">{t[option.labelKey]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Intake Instructions */}
          <div>
            <label
              htmlFor="medication-intake-instructions"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormInstructions}
            </label>
            <textarea
              id="medication-intake-instructions"
              value={formData.intakeInstructions || ""}
              onChange={(e) =>
                updateField("intakeInstructions", e.target.value)
              }
              placeholder={t.medxFormInstructionsPlaceholder}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Start and End Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="medication-start-date"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.medxFormStartDate}
              </label>
              <input
                id="medication-start-date"
                type="date"
                value={formData.startDate || ""}
                onChange={(e) => updateField("startDate", e.target.value)}
                className={fieldClass}
              />
            </div>

            <div>
              <label
                htmlFor="medication-end-date"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.medxFormEndDate}
              </label>
              <input
                id="medication-end-date"
                type="date"
                value={formData.endDate || ""}
                onChange={(e) => updateField("endDate", e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Box 3: Prescription */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-sm p-6 border-2 border-purple-300">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          {t.medxFormSectionPrescription}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Prescriber Name */}
          <div className="md:col-span-2">
            <label
              htmlFor="medication-prescriber-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormPrescriber}
            </label>
            <input
              id="medication-prescriber-name"
              type="text"
              value={formData.prescriberName || ""}
              onChange={(e) => updateField("prescriberName", e.target.value)}
              placeholder={t.medxFormPrescriberPlaceholder}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Prescription Country */}
          <div>
            <label
              htmlFor="medication-prescription-country"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormCountry}
            </label>
            <select
              id="medication-prescription-country"
              value={formData.prescriptionCountry || ""}
              onChange={(e) =>
                updateField("prescriptionCountry", e.target.value)
              }
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">{t.medxFormSelectCountry}</option>
              {terminologies?.prescriptionCountries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.display}
                </option>
              ))}
            </select>
          </div>

          {/* Prescription Reason */}
          <div>
            <label
              htmlFor="medication-prescription-reason"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t.medxFormReason}
            </label>
            <input
              id="medication-prescription-reason"
              type="text"
              value={formData.prescriptionPurpose || ""}
              onChange={(e) =>
                updateField("prescriptionPurpose", e.target.value)
              }
              placeholder={t.medxFormReasonPlaceholder}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Prescription Upload */}
          <div className="md:col-span-2">
            <div className="block text-sm font-medium text-gray-700 mb-2">
              {t.medxFormPrescriptionScan}
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" />
                {t.medxFormUploadPrescription}
              </button>
              {formData.prescriptionUrl && (
                <span className="text-sm text-green-600">
                  ✓ {t.medxFormPrescriptionUploaded}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t.medxFormPrescriptionHint}
            </p>
          </div>
        </div>

        {/* Self-Signed Info */}
        <NudgeBanner
          variant="warning"
          title={t.medxFormNoteTitle}
          live={false}
          className="mt-4 text-sm"
        >
          {t.medxFormSelfSignedNote}
        </NudgeBanner>
      </div>

      {/* BOX 4: Medication Images (Optional) */}
      <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg shadow-sm p-6 border-2 border-green-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-600 rounded-lg">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t.medxFormSectionImages}
            </h2>
            <p className="text-sm text-gray-600">{t.medxFormImagesHint}</p>
          </div>
          <span className="ml-auto text-xs text-green-700 bg-green-100 px-3 py-1 rounded-full font-medium">
            {t.optional}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Package Image */}
          <div>
            {/* Not a <label>: this names an upload region containing a
                preview, buttons and a conditionally-rendered file input,
                not a single form control. */}
            <span
              id="package-image-label"
              className="block text-sm font-medium text-gray-700 mb-3"
            >
              <Package className="inline w-4 h-4 mr-1" />
              {t.medxFormPackageImage}
            </span>
            {packagePreview ? (
              <div className="relative inline-block">
                <img
                  src={packagePreviewSrc ?? undefined}
                  alt={t.medxFormPackagePreviewAlt}
                  className="w-full h-48 object-cover rounded-lg border-4 border-green-500 shadow-md"
                />
                <button
                  type="button"
                  onClick={removePackageImage}
                  aria-label={t.medxFormRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="mt-2 text-xs text-gray-600 truncate">
                  {packageImage?.name}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-green-400 rounded-lg cursor-pointer bg-white hover:bg-green-50 transition-colors">
                  <Package className="h-12 w-12 text-green-500 mb-2" />
                  <span className="text-sm font-medium text-gray-700">
                    {t.medxFormUploadPackage}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    {t.medxFormImageFormats}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handlePackageImageChange}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setShowPackageCamera(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Camera className="h-5 w-5" />
                  {t.medxFormCaptureCamera}
                </button>
              </div>
            )}
          </div>

          {/* Medication/Pill Image */}
          <div>
            {/* Not a <label>: this names an upload region containing a
                preview, buttons and a conditionally-rendered file input,
                not a single form control. */}
            <span
              id="medication-image-label"
              className="block text-sm font-medium text-gray-700 mb-3"
            >
              <Pill className="inline w-4 h-4 mr-1" />
              {t.medxFormPillImage}
            </span>
            {medicationPreview ? (
              <div className="relative inline-block">
                <img
                  src={medicationPreviewSrc ?? undefined}
                  alt={t.medxFormPillPreviewAlt}
                  className="w-full h-48 object-cover rounded-lg border-4 border-teal-500 shadow-md"
                />
                <button
                  type="button"
                  onClick={removeMedicationImage}
                  aria-label={t.medxFormRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="mt-2 text-xs text-gray-600 truncate">
                  {medicationImage?.name}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-teal-400 rounded-lg cursor-pointer bg-white hover:bg-teal-50 transition-colors">
                  <Pill className="h-12 w-12 text-teal-500 mb-2" />
                  <span className="text-sm font-medium text-gray-700">
                    {t.medxFormUploadPill}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    {t.medxFormImageFormats}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleMedicationImageChange}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setShowMedicationCamera(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Camera className="h-5 w-5" />
                  {t.medxFormCaptureCamera}
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-600 mt-4 flex items-start gap-2">
          <span className="text-green-600">💡</span>
          <span>{t.medxFormImagesTip}</span>
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          {t.cancel}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving
            ? t.medxFormSaving
            : medicationId
              ? t.medxFormSaveChanges
              : t.medxFormSaveNew}
        </button>
      </div>

      {/* FHIR ID Info */}
      {medicationId && (
        <div className="text-xs text-gray-500 text-center">
          {t.medxFormFhirId}{" "}
          <code className="bg-gray-100 px-2 py-1 rounded">{medicationId}</code>
        </div>
      )}

      {/* Camera Capture Modals */}
      <CameraCapture
        isOpen={showPackageCamera}
        onClose={() => setShowPackageCamera(false)}
        onCapture={handlePackageCameraCapture}
        title={t.medxFormCapturePackageTitle}
      />

      <CameraCapture
        isOpen={showMedicationCamera}
        onClose={() => setShowMedicationCamera(false)}
        onCapture={handleMedicationCameraCapture}
        title={t.medxFormCapturePillTitle}
      />
    </form>
  );
};

export default MedicationForm;
